import { com } from '@eidu/web'
import React, { useMemo, useState } from 'react'
import { Button, Card, Group, Select, Stack, TextInput } from '@common/ui'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import ComplexPathField from './ComplexPathField'
import useRenderablePaths from '../misc/useRenderablePaths'
import { useCreateSemanticLink } from '../../io/useSemanticLinks'
import allSemanticLinkKeyConfigurations = com.eidu.entity.analytics.allSemanticLinkKeyConfigurations
import SemanticLinkKeyConfiguration = com.eidu.entity.analytics.SemanticLinkKeyConfiguration
import EntityType = com.eidu.entity.type.EntityType
import EntityPath = com.eidu.entity.path.EntityPath
import semanticLinkKeyFromString = com.eidu.entity.analytics.semanticLinkKeyFromString
import isAllowedNonPredefinedKey = com.eidu.entity.analytics.isAllowedNonPredefinedKey
import customSemanticLinkKeyPrefixes = com.eidu.entity.analytics.customSemanticLinkKeyPrefixes
import FieldType = com.eidu.entity.field.FieldType

const CUSTOM_KEY_SENTINEL = '__custom__'
const exampleKeys = customSemanticLinkKeyPrefixes
  .asJsReadonlyArrayView()
  .map((p) => `${p}my-key`)
  .join(' or ')

type AddSemanticLinkFormProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onSave: () => void
  onCancel: () => void
}

const fieldTypeOptions: FieldType[] = [FieldType.Reference, FieldType.Text, FieldType.Number, FieldType.Date]

const AddSemanticLinkForm = ({ entityType, entityTypes, onSave, onCancel }: AddSemanticLinkFormProps) => {
  const predefinedConfigs = useMemo<SemanticLinkKeyConfiguration[]>(
    () =>
      allSemanticLinkKeyConfigurations
        .asJsReadonlyArrayView()
        .filter(({ onTypeKind }) => onTypeKind.equals(entityType.kind)),
    [entityType.kind]
  )

  const [selectedKeyValue, setSelectedKeyValue] = useState('')
  const [customKeyInput, setCustomKeyInput] = useState('')
  const [keyError, setKeyError] = useState<string | null>(null)

  const [fieldType, setFieldType] = useState<FieldType>(FieldType.Reference)
  const [selectedTargetTypeId, setSelectedTargetTypeId] = useState(entityTypes.at(0)?.id)
  const [selectedPath, setSelectedPath] = useState<EntityPath>()
  const { save, isSaving } = useCreateSemanticLink(onSave)

  const isCustom = selectedKeyValue === CUSTOM_KEY_SENTINEL
  const selectedPredefined = !isCustom
    ? predefinedConfigs.find((c) => c.key.toString() === selectedKeyValue)
    : undefined

  const resolvedKey = useMemo(() => {
    if (selectedPredefined) return selectedPredefined.key
    if (!isCustom) return null
    if (predefinedConfigs.some((c) => c.key.toString() === customKeyInput)) return null
    try {
      const key = semanticLinkKeyFromString(customKeyInput)
      return isAllowedNonPredefinedKey(key) ? key : null
    } catch {
      return null
    }
  }, [selectedPredefined, isCustom, predefinedConfigs, customKeyInput])

  const effectiveFieldType = selectedPredefined ? selectedPredefined.fieldType : fieldType

  const availableTargetEntityTypes = entityTypes.filter(({ kind }) => {
    const toTypeKind = selectedPredefined?.toTypeKind ?? null
    return toTypeKind ? kind.equals(toTypeKind) : true
  })

  const targetType = selectedTargetTypeId && entityTypes.find(({ id }) => id.equals(selectedTargetTypeId))
  const renderablePaths = useRenderablePaths(entityType.id, targetType, entityTypes)

  const handleSave = () => {
    if (resolvedKey && selectedPath) save(resolvedKey, selectedPath)
  }

  return (
    <Card withBorder data-testid="semantic-link-form">
      <Stack gap="md">
        <Select
          label="Semantic link key"
          value={selectedKeyValue || null}
          onChange={(value) => {
            if (value === null) return
            setSelectedKeyValue(value)
            setCustomKeyInput('')
            setKeyError(null)
            setSelectedPath(undefined)
          }}
          data={[
            ...predefinedConfigs.map((config) => ({
              value: config.key.toString(),
              label: `${config.title} (${config.key.toString()})`,
            })),
            { value: CUSTOM_KEY_SENTINEL, label: 'Custom key...' },
          ]}
        />
        {isCustom && (
          <TextInput
            label="Custom key"
            placeholder={`e.g. ${exampleKeys}`}
            value={customKeyInput}
            error={keyError}
            onChange={(e) => {
              setCustomKeyInput(e.target.value)
              setKeyError(null)
            }}
            onBlur={() => {
              if (customKeyInput) {
                if (predefinedConfigs.some((c) => c.key.toString() === customKeyInput)) {
                  setKeyError('This key already exists as a predefined option — select it from the dropdown instead')
                } else {
                  try {
                    const key = semanticLinkKeyFromString(customKeyInput)
                    if (!isAllowedNonPredefinedKey(key)) {
                      setKeyError(`Keys in reserved namespaces must use a .custom. prefix, e.g. ${exampleKeys}`)
                    } else {
                      setKeyError(null)
                    }
                  } catch (e) {
                    setKeyError(`Invalid key: ${e instanceof Error ? e.message : String(e)}`)
                  }
                }
              }
            }}
          />
        )}
        {isCustom && (
          <Select
            label="Field type"
            value={fieldType.toString()}
            onChange={(value) => {
              const selected = fieldTypeOptions.find((ft) => ft.toString() === value)
              if (selected) {
                setFieldType(selected)
                setSelectedPath(undefined)
              }
            }}
            data={fieldTypeOptions.map((ft) => ft.toString())}
          />
        )}
        {selectedPredefined && (
          <Select
            label="Field type"
            value={selectedPredefined.fieldType.toString()}
            data={[selectedPredefined.fieldType.toString()]}
            disabled
          />
        )}
        {(selectedPredefined || isCustom) && (
          <>
            <EntityTypeField
              entityTypeId={selectedTargetTypeId}
              onChange={(type) => {
                setSelectedTargetTypeId(type.id)
                setSelectedPath(undefined)
              }}
              types={availableTargetEntityTypes}
              label="Target type"
            />
            <ComplexPathField
              entityTypeId={entityType.id}
              targetType={targetType}
              selectedPath={selectedPath}
              setSelectedPath={setSelectedPath}
              renderablePaths={renderablePaths}
              fieldType={effectiveFieldType}
            />
          </>
        )}
        <Group gap="md" justify="flex-end">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !selectedPath || !resolvedKey}>
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}

export default AddSemanticLinkForm
